import React, {useEffect, useRef} from "react";

const GOLD = ["#ffd86b", "#f5c542", "#fff3b0"];
const CYAN = ["#6fe3ff", "#8fe6ff", "#b8f3ff"];
const VIOLET = ["#c46bff", "#d99bff", "#a54dff"];
const PERIWINKLE = ["#8a8cff", "#a9aaff"];
const PALETTES = [GOLD, CYAN, VIOLET, PERIWINKLE];
const SHAPES = ["peony", "ring", "willow", "palm", "crossette", "double"];

const GRAVITY = 0.045;
// simulation speed, > 1 makes the whole show faster
const SPEED = 1.8;

function rand(min, max) {
    return min + Math.random() * (max - min);
}

function pick(list) {
    return list[Math.floor(Math.random() * list.length)];
}

// one, two or three palettes mixed for a burst
function burstColors() {
    const first = pick(PALETTES);
    const roll = Math.random();
    if (roll < 0.4) {
        return first;
    }
    const second = pick(PALETTES.filter(p => p !== first));
    if (roll < 0.8) {
        return [...first, ...second];
    }
    return [...first, ...second, ...pick(PALETTES.filter(p => p !== first && p !== second))];
}

/*
 * Fireworks drawn on a full screen canvas, randomized on every mount
 * (number of rockets, timing, positions, shapes and colors) so that each category gets a different show.
 */
export const Fireworks2026 = ({onFirstExplosion}) => {

    const canvasRef = useRef(undefined);
    const onFirstExplosionRef = useRef(onFirstExplosion);
    onFirstExplosionRef.current = onFirstExplosion;

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const dpr = window.devicePixelRatio || 1;
        let width, height;

        function resize() {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width * dpr;
            canvas.height = height * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }

        resize();
        window.addEventListener("resize", resize);

        const scale = height / 900;
        const rockets = [];
        const particles = [];

        // the show is centered on the winner
        const winner = document.querySelector(".reveal-podium-column.rank0 .reveal-podium-game img");
        const box = winner ? winner.getBoundingClientRect() : undefined;
        const centerX = box ? box.left + box.width / 2 : width * 0.65;
        // the winner is still at the start of its pop animation, 6vh lower than its final place
        const centerY = box ? box.top + box.height / 2 - height * 0.06 : height * 0.35;

        // rockets come out from behind the bottom bar of the other games
        const bottomBar = document.querySelector(".reveal-others");
        const launchY = bottomBar ? bottomBar.getBoundingClientRect().top : height;

        // launch plan of the show
        const launches = [];
        const rocketCount = Math.round(rand(3, 6));
        const duration = rand(1200, 2200);
        for (let i = 0; i < rocketCount; i++) {
            launches.push(i === 0 ? 0 : rand(250, duration));
        }
        // sometimes a final salvo
        if (Math.random() < 0.4) {
            const salvo = duration + rand(100, 300);
            for (let i = 0; i < Math.round(rand(2, 3)); i++) {
                launches.push(salvo + rand(0, 150));
            }
        }
        launches.sort((a, b) => a - b);

        let rocketsLaunched = 0;

        function launchRocket() {
            // the first rocket goes right on the winner, it reveals it when it explodes
            const first = rocketsLaunched++ === 0;
            const targetX = first ? centerX : centerX + rand(-1, 1) * width * 0.14;
            const x = first ? centerX : targetX + rand(-1, 1) * width * 0.05;
            const targetY = first ? centerY : Math.max(height * 0.06, centerY + rand(-1, 1) * height * 0.16);
            const vy = -Math.sqrt(2 * GRAVITY * Math.max(launchY - targetY, 50)) * rand(0.98, 1.02);
            rockets.push({
                x, y: launchY, vx: (targetX - x) * GRAVITY / -vy, vy,
                colors: burstColors(),
                shape: pick(SHAPES),
                first,
                size: rand(0.8, 1.3),
            });
        }

        function addParticle(x, y, angle, speed, color, options = {}) {
            particles.push({
                x, y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                color,
                life: 0,
                maxLife: options.maxLife || rand(60, 100),
                drag: options.drag || 0.975,
                gravity: options.gravity === undefined ? GRAVITY * 0.6 : options.gravity,
                radius: options.radius || rand(1.2, 2.4) * scale,
                twinkle: options.twinkle === undefined ? Math.random() < 0.3 : options.twinkle,
                split: options.split,
                trail: options.trail,
            });
        }

        function explode(rocket) {
            if (rocket.first && onFirstExplosionRef.current) {
                onFirstExplosionRef.current();
            }
            const {x, y, colors, size} = rocket;
            const s = scale * size;
            switch (rocket.shape) {
                case "ring": {
                    const count = Math.round(rand(40, 70));
                    const speed = rand(4, 6) * s;
                    const tilt = rand(0.4, 1);
                    const rotation = rand(0, Math.PI);
                    for (let i = 0; i < count; i++) {
                        const a = (i / count) * Math.PI * 2;
                        const px = Math.cos(a), py = Math.sin(a) * tilt;
                        const angle = Math.atan2(px * Math.sin(rotation) + py * Math.cos(rotation), px * Math.cos(rotation) - py * Math.sin(rotation));
                        addParticle(x, y, angle, speed * Math.hypot(px, py), pick(colors), {drag: 0.97});
                    }
                    break;
                }
                case "willow": {
                    const count = Math.round(rand(80, 120));
                    for (let i = 0; i < count; i++) {
                        addParticle(x, y, rand(0, Math.PI * 2), rand(1, 4.5) * s, pick(GOLD), {
                            maxLife: rand(140, 200), drag: 0.985, gravity: GRAVITY * 0.35, radius: rand(1, 1.8) * scale, twinkle: true,
                        });
                    }
                    break;
                }
                case "palm": {
                    const count = Math.round(rand(7, 12));
                    for (let i = 0; i < count; i++) {
                        addParticle(x, y, (i / count) * Math.PI * 2 + rand(-0.1, 0.1), rand(5, 7) * s, pick(colors), {
                            maxLife: rand(90, 120), drag: 0.98, radius: 3 * scale, trail: true, twinkle: false,
                        });
                    }
                    break;
                }
                case "crossette": {
                    const count = Math.round(rand(12, 20));
                    for (let i = 0; i < count; i++) {
                        addParticle(x, y, rand(0, Math.PI * 2), rand(3, 5) * s, pick(colors), {
                            maxLife: rand(35, 50), radius: 2.4 * scale, split: true, twinkle: false,
                        });
                    }
                    break;
                }
                case "double": {
                    const inner = pick(PALETTES);
                    for (let i = 0; i < 70; i++) {
                        addParticle(x, y, rand(0, Math.PI * 2), rand(4, 6.5) * s, pick(colors));
                    }
                    for (let i = 0; i < 40; i++) {
                        addParticle(x, y, rand(0, Math.PI * 2), rand(1, 2.5) * s, pick(inner), {twinkle: true});
                    }
                    break;
                }
                default: {
                    // peony
                    const count = Math.round(rand(90, 150));
                    for (let i = 0; i < count; i++) {
                        addParticle(x, y, rand(0, Math.PI * 2), Math.pow(Math.random(), 0.4) * rand(5, 7) * s, pick(colors));
                    }
                }
            }
            // flash
            addParticle(x, y, 0, 0, "#ffffff", {maxLife: 8, radius: 14 * scale * size, gravity: 0, twinkle: false});
        }

        const start = performance.now();
        let frame;

        function tick(now) {
            const elapsed = now - start;
            while (launches.length && launches[0] <= elapsed) {
                launches.shift();
                launchRocket();
            }

            // fade what was drawn before, which leaves trails
            ctx.globalCompositeOperation = "destination-out";
            ctx.fillStyle = "rgba(0, 0, 0, 0.22)";
            ctx.fillRect(0, 0, width, height);
            ctx.globalCompositeOperation = "lighter";

            for (let i = rockets.length - 1; i >= 0; i--) {
                const r = rockets[i];
                r.x += r.vx * SPEED;
                r.y += r.vy * SPEED;
                r.vy += GRAVITY * SPEED;
                ctx.fillStyle = "#fff3b0";
                ctx.beginPath();
                ctx.arc(r.x, r.y, 2.2 * scale, 0, Math.PI * 2);
                ctx.fill();
                if (Math.random() < 0.7) {
                    addParticle(r.x, r.y, rand(0, Math.PI * 2), rand(0, 0.6), pick(GOLD), {maxLife: rand(15, 30), radius: 1.2 * scale, twinkle: false});
                }
                if (r.vy >= -0.5) {
                    rockets.splice(i, 1);
                    explode(r);
                }
            }

            for (let i = particles.length - 1; i >= 0; i--) {
                const p = particles[i];
                p.life += SPEED;
                const drag = Math.pow(p.drag, SPEED);
                p.vx *= drag;
                p.vy = p.vy * drag + p.gravity * SPEED;
                p.x += p.vx * SPEED;
                p.y += p.vy * SPEED;
                if (p.trail && Math.random() < 0.5) {
                    addParticle(p.x, p.y, 0, 0, p.color, {maxLife: 25, radius: p.radius * 0.6, gravity: 0.02, twinkle: false});
                }
                if (p.life >= p.maxLife) {
                    if (p.split) {
                        for (let k = 0; k < 4; k++) {
                            addParticle(p.x, p.y, k * Math.PI / 2 + rand(-0.2, 0.2), rand(1.5, 2.5) * scale, p.color, {maxLife: rand(30, 45), twinkle: true});
                        }
                    }
                    particles.splice(i, 1);
                    continue;
                }
                let alpha = 1 - p.life / p.maxLife;
                if (p.twinkle && Math.random() < 0.35) {
                    alpha *= 0.2;
                }
                ctx.fillStyle = p.color;
                // neon halo, then the core
                ctx.globalAlpha = Math.max(0, alpha) * 0.18;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.radius * 3.5, 0, Math.PI * 2);
                ctx.fill();
                ctx.globalAlpha = Math.max(0, alpha);
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.globalAlpha = 1;

            if (launches.length || rockets.length || particles.length) {
                frame = requestAnimationFrame(tick);
            } else {
                ctx.clearRect(0, 0, width, height);
            }
        }

        frame = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("resize", resize);
        };
    }, []);

    return <canvas ref={canvasRef} className="reveal-fireworks"/>;
}
